import React, { useState } from "react";
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  DownOutlined,
  FileAddOutlined,
  MailOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Dropdown,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { productClientActions } from "@fitness-app/app-store";
import { PermissionKey, PermissionScope } from "@fitness-app/data-models";
import { type ErasureReportEntry, type ErasureResult } from "@fitness-app/data-models/entities/GdprErasure";
import { canConfirmManualOrder } from "@fitness-app/data-models/entities/Order";
import { PaymentClient } from "@fitness-app/data-models/entities/Product";
import { APP_ACCESS_PRODUCT_TYPES } from "@fitness-app/utils/src/products/hasLiveOrScheduledAccess";

import { usePermission, usePermissionRequirement } from "~/hooks/usePermission";
import { useProductsList } from "~/modules/Dashboard/AppStats/hooks/useProductsList";
import { dashboardPermissionRequirements } from "~/modules/Dashboard/dashboardPermissionRequirements";
import { OrderPaidAtCell } from "~/modules/Products/helpers/OrderPaidAtCell";
import { useConfirmManualPayment } from "~/modules/Products/hooks/useConfirmManualPayment";
import { adminApi, supabase, useAppDispatch } from "~/store/initializeStore";
import { AddCustomerToProductModal } from "./AddCustomerToProductModal";
import { CustomerExpandedRow } from "./CustomerExpandedRow";
import { ManualOrderModal } from "./ManualOrderModal";
import { useCustomerProductClients } from "./useCustomerProductClients";
import { type CustomerListItem } from "./useProductsCustomers";

type CustomerOrder = {
  id: string;
  createdAt: string | null;
  clientEmail: string | null;
  status: string;
  paymentType: string | null;
  paymentGateway: string | null;
  product: string | null;
  price: { amount?: number; currency?: string; productName?: string; priceDescription?: string | null } | null;
  items: unknown;
  paidAt: string | null;
};

const orderStatusColor: Record<string, string> = {
  PAID: "green",
  FREE_FINALIZED: "cyan",
  CREATED: "blue",
  FAILED: "red",
  REFUNDED: "orange",
  CANCELED: "default",
};

const formatMoney = (value: number | null | undefined, currency = "PLN") =>
  typeof value === "number"
    ? new Intl.NumberFormat("pl-PL", {
        style: "currency",
        currency: (currency || "PLN").toUpperCase(),
        minimumFractionDigits: 2,
      }).format(value / 100)
    : "-";

// Strip characters that are structural in PostgREST `or(...)` filter strings
// and escape LIKE wildcards so e.g. underscores in e-mails match literally.
const escapeIlikeEmail = (email: string) =>
  email
    .replace(/[,()]/g, "")
    .replace(/[%_]/g, (wildcard) => `\\${wildcard}`)
    .trim();

const getOrderProductName = (order: CustomerOrder): string | null => {
  if (order.price?.productName) return order.price.productName;

  let items: { productName?: string | null }[] | null = null;
  try {
    items = (typeof order.items === "string" ? JSON.parse(order.items) : order.items) as
      | { productName?: string | null }[]
      | null;
  } catch {
    items = null;
  }

  return items?.[0]?.productName ?? null;
};

interface CustomerDetailsProps {
  /** Podany wprost, gdy ekran jest osadzony (drawer) zamiast stać na trasie. */
  customerId?: string;
  /** Tryb osadzony: bez powrotu do listy i bez wymogu pełnej zakładki „Klienci". */
  embedded?: boolean;
}

export const CustomerDetails: React.FC<CustomerDetailsProps> = ({
  customerId: customerIdFromProps,
  embedded = false,
}) => {
  const { t } = useTranslation(["products", "common"]);
  const { customerId: customerIdFromRoute } = useParams<{ customerId: string }>();
  const customerId = customerIdFromProps ?? customerIdFromRoute;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const [manualOrderOpen, setManualOrderOpen] = useState(false);
  const [addToProductOpen, setAddToProductOpen] = useState(false);
  const [aliasModalOpen, setAliasModalOpen] = useState(false);
  const [aliasForm] = Form.useForm<{ alias: string }>();
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailForm] = Form.useForm<{ newEmail: string; sendEmailNotification: boolean }>();
  const [gdprEraseOpen, setGdprEraseOpen] = useState(false);
  const [gdprEraseForm] = Form.useForm<{ confirmEmail: string }>();
  const [erasureResult, setErasureResult] = useState<ErasureResult | null>(null);
  // Ten sam wymóg co zakładka „Klienci" i co guard na trasie. Operacje na
  // zamówieniach mają osobną bramkę — konto operacyjne bez products.manage
  // ogląda historię, ale jej nie rusza.
  const hasFullCustomersAccess = usePermissionRequirement(dashboardPermissionRequirements.productsCustomers);
  const canReadClients = usePermission(PermissionKey.CLIENTS_READ);
  // Osadzony w profilu klientki ekran otwiera każdy, kto i tak widzi tę klientkę —
  // wymóg zakładki „Klienci" (clients.write ALL) bramkuje tylko samodzielną trasę.
  const hasAccess = embedded ? canReadClients : hasFullCustomersAccess;
  const canManageOrders = usePermission(PermissionKey.PRODUCTS_MANAGE, PermissionScope.ALL);
  // TRE-2043: erasure z art. 17 RODO — osobny klucz (owner + administracja),
  // lustro guardu na POST /erase-customer-data w admin-service.
  const canEraseClientData = usePermission(PermissionKey.CLIENTS_ERASE, PermissionScope.ALL);

  const customerQuery = useQuery({
    queryKey: ["customerDetails", customerId],
    enabled: Boolean(customerId) && hasAccess,
    retry: false,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("customer")
        .select("*")
        .eq("id", customerId ?? "")
        .maybeSingle();

      if (error) throw new Error(error.message);
      return data as unknown as (Omit<CustomerListItem, "bundlesCount"> & { updatedAt: string | null }) | null;
    },
  });

  const customer = customerQuery.data;
  const allEmails = [customer?.email, ...(customer?.emailAliases ?? [])]
    .filter((email): email is string => Boolean(email))
    .map((email) => email.trim().toLowerCase())
    .filter((email, index, list) => Boolean(email) && list.indexOf(email) === index);

  // The customer-level email change reuses the product-scoped endpoint with
  // applyToAllProducts=true, so it needs one product_client whose email matches the
  // customer's current email as the primary anchor (that's the auth account being renamed).
  const { list: customerProducts } = useCustomerProductClients(customerId ?? "", Boolean(customerId) && hasAccess);
  // TRE-2509: erasure odcina logowanie, więc żywy dostęp low/high ticket musi
  // być najpierw rozliczony (content i addony mogą zostać). Serwer pilnuje tego
  // samego, dodatkowo po adresie e-mail.
  const activeAccesses = customerProducts.filter(
    (productClient) =>
      productClient.status === "active" && APP_ACCESS_PRODUCT_TYPES.includes(productClient.productType),
  );
  // Ten sam cache co CustomerExpandedRow niżej na tym ekranie.
  const { list: productsList } = useProductsList();
  const primaryProductForEmailChange = customer?.email
    ? customerProducts.find(
        (productClient) => productClient.email?.trim().toLowerCase() === customer.email?.trim().toLowerCase(),
      )
    : undefined;

  const ordersQuery = useQuery({
    queryKey: ["customerOrders", allEmails],
    enabled: allEmails.length > 0,
    retry: false,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("product_order")
        // Only `paidAt` out of `metadata`: the bag carries the whole checkout session and this lists up to 100 orders.
        .select(
          "id, createdAt, status, paymentType, paymentGateway, product, price, items, clientEmail, paidAt:metadata->>paidAt",
        )
        .or(allEmails.map((email) => `clientEmail.ilike.${escapeIlikeEmail(email)}`).join(","))
        .order("createdAt", { ascending: false })
        .limit(100);

      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as CustomerOrder[];
    },
  });

  const confirmManualPayment = useConfirmManualPayment();

  const markOrderAsPaid = async (order: CustomerOrder) => {
    if (!order.product || !order.clientEmail) {
      void message.warning(t("customersTab.details.markAsPaidMissingData"));
      return;
    }

    try {
      await confirmManualPayment.mutateAsync({
        productId: order.product,
        orderId: order.id,
        clientEmail: order.clientEmail,
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["customerOrders"] }),
        queryClient.invalidateQueries({ queryKey: ["customerBundles"] }),
        queryClient.invalidateQueries({ queryKey: ["customerDetails"] }),
      ]);
    } catch {
      // error toast is shown by useConfirmManualPayment
    }
  };

  const addAliasMutation = useMutation({
    mutationFn: async ({ alias }: { alias: string }) => {
      if (!customer) return;

      const normalized = alias.trim().toLowerCase();

      if (!normalized || normalized === customer.email?.toLowerCase()) {
        throw new Error(t("customersTab.details.aliasModal.mustDiffer"));
      }

      const nextAliases = Array.from(new Set([...(customer.emailAliases ?? []), normalized]));

      const { error } = await supabase
        .from("customer")
        .update({ emailAliases: nextAliases, updatedAt: new Date().toISOString() } as never)
        .eq("id", customer.id);

      if (error) throw new Error(error.message);
    },
    onSuccess: async () => {
      setAliasModalOpen(false);
      aliasForm.resetFields();
      await queryClient.invalidateQueries({ queryKey: ["customerDetails"] });
      await queryClient.invalidateQueries({ queryKey: ["customerOrders"] });
      await queryClient.invalidateQueries({ queryKey: ["customerBundles"] });
      void message.success(t("customersTab.details.aliasModal.added"));
    },
    onError: (error: Error) => {
      void message.error(error.message);
    },
  });

  const changeEmailMutation = useMutation({
    mutationFn: async ({ newEmail, sendEmailNotification }: { newEmail: string; sendEmailNotification: boolean }) => {
      if (!customer?.email) {
        throw new Error(t("customersTab.details.changeEmailModal.noEmail"));
      }

      const normalized = newEmail.trim().toLowerCase();

      if (normalized === customer.email.trim().toLowerCase()) {
        throw new Error(t("customersTab.details.changeEmailModal.mustDiffer"));
      }

      if (!primaryProductForEmailChange) {
        throw new Error(t("customersTab.details.changeEmailModal.noPrimaryProduct"));
      }

      // applyToAllProducts re-points the whole customer (login account, every product and
      // order) — the canonical fix for a wrong address. The primary product just anchors the
      // account rename to the customer's current email.
      await dispatch(
        productClientActions.changeProductClientEmail({
          oldEmail: customer.email,
          newEmail: normalized,
          productId: primaryProductForEmailChange.productId,
          tasksForOldEmail: [],
          tasksForNewEmail: [],
          sendEmailNotification,
          applyToAllProducts: true,
        }),
      ).unwrap();
    },
    onSuccess: async () => {
      setEmailModalOpen(false);
      emailForm.resetFields();
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["customerDetails"] }),
        queryClient.invalidateQueries({ queryKey: ["customerOrders"] }),
        queryClient.invalidateQueries({ queryKey: ["customerBundles"] }),
        queryClient.invalidateQueries({ queryKey: ["customerProductClients"] }),
      ]);
      void message.success(t("customersTab.details.changeEmailModal.success"));
    },
    onError: (error: unknown) => {
      // The thunk rejects with the backend message (rejectWithValue); show it when present.
      void message.error(typeof error === "string" && error ? error : (error as Error)?.message);
    },
  });

  const gdprEraseMutation = useMutation({
    mutationFn: async ({ confirmEmail }: { confirmEmail: string }) => {
      const expected = (customer?.email ?? customer?.id ?? "").trim().toLowerCase();

      if (confirmEmail.trim().toLowerCase() !== expected) {
        throw new Error(t("customersTab.details.gdprErase.confirmMismatch"));
      }

      const { data } = await adminApi.post<ErasureResult>("/erase-customer-data", { customerId });
      return data;
    },
    onSuccess: async (result) => {
      setGdprEraseOpen(false);
      gdprEraseForm.resetFields();
      setErasureResult(result);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["customerDetails"] }),
        queryClient.invalidateQueries({ queryKey: ["customerOrders"] }),
        queryClient.invalidateQueries({ queryKey: ["customerBundles"] }),
        queryClient.invalidateQueries({ queryKey: ["customerProductClients"] }),
      ]);
    },
    onError: (error: unknown) => {
      const apiMessage = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      void message.error(apiMessage ?? (error as Error)?.message ?? t("customersTab.details.gdprErase.failed"));
    },
  });

  if (!hasAccess) {
    return (
      <Card>
        <Space direction="vertical" align="center" style={{ width: "100%" }}>
          <Typography.Text>{t("customersTab.details.noAccess")}</Typography.Text>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/products")}>
            {t("customersTab.details.backToList")}
          </Button>
        </Space>
      </Card>
    );
  }

  if (!customerQuery.isLoading && !customer) {
    return (
      <Card>
        <Space direction="vertical" align="center" style={{ width: "100%" }}>
          <Typography.Text>{t("customersTab.details.notFound")}</Typography.Text>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/products?tab=customers")}>
            {t("customersTab.details.backToList")}
          </Button>
        </Space>
      </Card>
    );
  }

  const fullName = `${customer?.firstName ?? ""} ${customer?.lastName ?? ""}`.trim();
  // Klient w produkcie bywa bez konta w aplikacji — link pokazujemy tylko, gdy
  // któryś z jego pakietów jest już spięty z profilem klientki.
  const traineeId = customerProducts.find((productClient) => productClient.traineeId)?.traineeId ?? null;

  return (
    <Space direction="vertical" size={20} style={{ width: "100%" }}>
      <Card loading={customerQuery.isLoading}>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <Space align="center">
            {embedded ? null : (
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/products?tab=customers")} />
            )}
            <div>
              <Typography.Title level={4} className="!mb-0">
                {fullName || customer?.email || t("customersTab.details.clientFallback")}
              </Typography.Title>
              <Typography.Text type="secondary">{customer?.email}</Typography.Text>
            </div>
          </Space>
          {(canManageOrders || canEraseClientData) && (
            <Dropdown
              trigger={["click"]}
              menu={{
                items: [
                  ...(canManageOrders
                    ? [
                        {
                          key: "changeEmail",
                          icon: <MailOutlined />,
                          label: t("customersTab.details.changeEmail"),
                          onClick: () => {
                            if (!customer?.email) {
                              void message.warning(t("customersTab.details.noEmailWarning"));
                              return;
                            }
                            emailForm.setFieldsValue({ newEmail: "", sendEmailNotification: true });
                            setEmailModalOpen(true);
                          },
                        },
                        {
                          key: "addToProduct",
                          icon: <PlusOutlined />,
                          label: t("customersTab.details.addToProduct.button"),
                          onClick: () => {
                            if (!customer?.email) {
                              void message.warning(t("customersTab.details.noEmailWarning"));
                              return;
                            }
                            setAddToProductOpen(true);
                          },
                        },
                        {
                          key: "createManualOrder",
                          icon: <FileAddOutlined />,
                          label: t("customersTab.details.createManualOrder"),
                          onClick: () => {
                            if (!customer?.email) {
                              void message.warning(t("customersTab.details.noEmailWarning"));
                              return;
                            }
                            setManualOrderOpen(true);
                          },
                        },
                      ]
                    : []),
                  ...(canEraseClientData
                    ? [
                        ...(canManageOrders ? [{ type: "divider" as const }] : []),
                        {
                          key: "gdprErase",
                          icon: <DeleteOutlined />,
                          danger: true,
                          label: t("customersTab.details.gdprErase.button"),
                          onClick: () => {
                            gdprEraseForm.resetFields();
                            // Archiwizacja dostępu w szufladzie produktu idzie przez Reduxa
                            // i nie odświeża tej listy, a od niej zależy blokada usunięcia.
                            void queryClient.invalidateQueries({ queryKey: ["customerProductClients", customerId] });
                            setGdprEraseOpen(true);
                          },
                        },
                      ]
                    : []),
                ],
              }}
            >
              <Button>
                {t("customersTab.details.actionsMenu")} <DownOutlined />
              </Button>
            </Dropdown>
          )}
        </div>

        <Descriptions size="small" column={{ xs: 1, md: 3 }}>
          <Descriptions.Item label={t("customersTab.details.emailAliases")}>
            <Space size={4} wrap>
              {(customer?.emailAliases ?? []).map((alias) => (
                <Tag key={alias}>{alias}</Tag>
              ))}
              {canManageOrders && (
                <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={() => setAliasModalOpen(true)}>
                  {t("customersTab.details.addAlias")}
                </Button>
              )}
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label={t("customersTab.details.phone")}>{customer?.phoneNumber ?? "-"}</Descriptions.Item>
          <Descriptions.Item label={t("customersTab.details.source")}>
            {customer?.source ? <Tag>{customer.source}</Tag> : "-"}
          </Descriptions.Item>
          <Descriptions.Item label={t("customersTab.details.totalRevenue")}>
            {formatMoney(customer?.totalRevenue ?? 0)}
          </Descriptions.Item>
          <Descriptions.Item label={t("customersTab.details.createdAt")}>
            {customer?.createdAt ? dayjs(customer.createdAt).format("DD.MM.YYYY HH:mm") : "-"}
          </Descriptions.Item>
          {canManageOrders ? (
            <Descriptions.Item label={t("customersTab.details.stripeId")}>
              {customer?.stripeCustomerId ? (
                <Typography.Text copyable style={{ fontSize: 12 }}>
                  {customer.stripeCustomerId}
                </Typography.Text>
              ) : (
                "-"
              )}
            </Descriptions.Item>
          ) : null}
          <Descriptions.Item label={t("customersTab.details.traineeProfile")}>
            {traineeId ? (
              <Button type="link" className="!h-auto !px-0" onClick={() => navigate(`/trainee/details/${traineeId}`)}>
                {t("customersTab.details.openTraineeProfile")}
              </Button>
            ) : (
              "-"
            )}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card
        loading={ordersQuery.isInitialLoading}
        title={t("customersTab.details.orders", { count: ordersQuery.data?.length ?? 0 })}
      >
        <Table<CustomerOrder>
          rowKey="id"
          size="small"
          dataSource={ordersQuery.data ?? []}
          pagination={false}
          scroll={{ x: true }}
          locale={{ emptyText: t("common:noData") }}
        >
          <Table.Column<CustomerOrder>
            title={t("customersTab.details.orderTable.date")}
            dataIndex="createdAt"
            render={(value: string | null) => (value ? dayjs(value).format("DD.MM.YYYY HH:mm") : "-")}
          />
          <Table.Column<CustomerOrder>
            title={t("customersTab.details.orderTable.product")}
            render={(_, row) => getOrderProductName(row) ?? "-"}
          />
          <Table.Column<CustomerOrder>
            title={t("customersTab.details.orderTable.status")}
            dataIndex="status"
            render={(value: string) => (
              <Tag color={orderStatusColor[value] ?? "default"}>
                {t(`productTransactions.type.${value.toLowerCase()}`, { defaultValue: value })}
              </Tag>
            )}
          />
          <Table.Column<CustomerOrder>
            title={t("productTransactions.tableHeader.paidAt")}
            key="paidAt"
            render={(_, row) => <OrderPaidAtCell order={{ status: row.status, metadata: { paidAt: row.paidAt } }} />}
          />
          <Table.Column<CustomerOrder>
            title={t("customersTab.details.orderTable.paymentType")}
            dataIndex="paymentType"
            render={(value: string | null) => (value ? t(`paymentTypeOptions.${value}`, { defaultValue: value }) : "-")}
          />
          <Table.Column<CustomerOrder>
            title={t("customersTab.details.orderTable.amount")}
            render={(_, row) => formatMoney(row.price?.amount ?? null, row.price?.currency)}
          />
          <Table.Column<CustomerOrder>
            title={t("customersTab.details.orderTable.gateway")}
            dataIndex="paymentGateway"
            render={(value: string | null) =>
              value ? t(`productTransactions.paymentGateway.${value.toLowerCase()}`, { defaultValue: value }) : "-"
            }
          />
          <Table.Column<CustomerOrder>
            title={t("customersTab.details.orderTable.orderId")}
            dataIndex="id"
            render={(value: string) => (
              <Typography.Text copyable={{ text: value }} style={{ fontSize: 12 }}>
                {value.slice(0, 8)}…
              </Typography.Text>
            )}
          />
          <Table.Column<CustomerOrder>
            title={t("customersTab.details.orderTable.actions")}
            key="actions"
            render={(_, row) =>
              canManageOrders && row.paymentGateway === PaymentClient.MANUAL && canConfirmManualOrder(row.status) ? (
                <Popconfirm
                  title={t("customersTab.details.markAsPaidConfirmTitle")}
                  description={t("customersTab.details.markAsPaidConfirmDescription")}
                  okText={t("common:yes")}
                  cancelText={t("common:no")}
                  onConfirm={() => void markOrderAsPaid(row)}
                >
                  <Button
                    type="primary"
                    size="small"
                    icon={<CheckCircleOutlined />}
                    loading={confirmManualPayment.isLoading && confirmManualPayment.variables?.orderId === row.id}
                  >
                    {t("customersTab.details.markAsPaid")}
                  </Button>
                </Popconfirm>
              ) : null
            }
          />
        </Table>
      </Card>

      {customer ? (
        <Card title={t("customersTab.details.bundlesAndAccess")}>
          <CustomerExpandedRow customer={{ ...customer, bundlesCount: 0 }} showBillingData={canManageOrders} />
        </Card>
      ) : null}

      {canManageOrders ? (
        <AddCustomerToProductModal
          customer={
            customer?.email
              ? {
                  email: customer.email,
                  firstName: customer.firstName ?? null,
                  lastName: customer.lastName ?? null,
                  phoneNumber: customer.phoneNumber ?? null,
                }
              : null
          }
          customerProducts={customerProducts}
          open={addToProductOpen}
          onClose={() => setAddToProductOpen(false)}
          onScheduled={() => {
            void queryClient.invalidateQueries({ queryKey: ["customerProductClients", customerId] });
            void queryClient.invalidateQueries({ queryKey: ["customerDetails"] });
          }}
        />
      ) : null}

      {canManageOrders ? (
        <ManualOrderModal
          clientEmail={customer?.email ?? null}
          open={manualOrderOpen}
          onClose={() => setManualOrderOpen(false)}
          onCreated={() => {
            void queryClient.invalidateQueries({ queryKey: ["customerOrders"] });
            void queryClient.invalidateQueries({ queryKey: ["customerDetails"] });
          }}
        />
      ) : null}

      <Modal
        title={t("customersTab.details.aliasModal.title")}
        open={aliasModalOpen}
        onCancel={() => setAliasModalOpen(false)}
        onOk={() => aliasForm.submit()}
        okText={t("customersTab.details.aliasModal.submit")}
        confirmLoading={addAliasMutation.isLoading}
        destroyOnClose
      >
        <Typography.Paragraph type="secondary">{t("customersTab.details.aliasModal.description")}</Typography.Paragraph>
        <Form form={aliasForm} layout="vertical" onFinish={(values) => addAliasMutation.mutate(values)}>
          <Form.Item
            name="alias"
            label={t("customersTab.details.aliasModal.emailLabel")}
            rules={[
              { required: true, message: t("customersTab.details.aliasModal.emailRequired") },
              { type: "email", message: t("customersTab.details.aliasModal.emailInvalid") },
            ]}
          >
            <Input placeholder="alias@example.com" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t("customersTab.details.changeEmailModal.title")}
        open={emailModalOpen}
        onCancel={() => setEmailModalOpen(false)}
        onOk={() => emailForm.submit()}
        okText={t("customersTab.details.changeEmailModal.submit")}
        confirmLoading={changeEmailMutation.isLoading}
        destroyOnClose
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t("customersTab.details.changeEmailModal.description")}
        />
        {!primaryProductForEmailChange ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={t("customersTab.details.changeEmailModal.noPrimaryProduct")}
          />
        ) : null}
        <Form
          form={emailForm}
          layout="vertical"
          initialValues={{ sendEmailNotification: true }}
          onFinish={(values) => changeEmailMutation.mutate(values)}
        >
          <Form.Item label={t("customersTab.details.changeEmailModal.currentEmail")}>
            <Input value={customer?.email ?? ""} disabled />
          </Form.Item>
          <Form.Item
            name="newEmail"
            label={t("customersTab.details.changeEmailModal.newEmailLabel")}
            rules={[
              { required: true, message: t("customersTab.details.changeEmailModal.emailRequired") },
              {
                type: "email",
                message: t("customersTab.details.changeEmailModal.emailInvalid"),
                transform: (value: string) => value.trim(),
              },
            ]}
          >
            <Input placeholder={t<string>("customersTab.details.changeEmailModal.newEmailPlaceholder")} />
          </Form.Item>
          <Form.Item
            name="sendEmailNotification"
            label={t("customersTab.details.changeEmailModal.sendNotification")}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t("customersTab.details.gdprErase.title")}
        open={gdprEraseOpen}
        onCancel={() => setGdprEraseOpen(false)}
        onOk={() => gdprEraseForm.submit()}
        okText={t("customersTab.details.gdprErase.submit")}
        okButtonProps={{ danger: true, disabled: activeAccesses.length > 0 }}
        confirmLoading={gdprEraseMutation.isLoading}
        destroyOnClose
      >
        {activeAccesses.length > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={t("customersTab.details.gdprErase.activeAccessTitle")}
            description={t("customersTab.details.gdprErase.activeAccessDescription", {
              products: activeAccesses
                .map(
                  (productClient) =>
                    productsList.find((product) => product.value === productClient.productId)?.label ??
                    productClient.productId,
                )
                .join(", "),
            })}
          />
        )}
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={t("customersTab.details.gdprErase.warningTitle")}
          description={
            <>
              <div>{t("customersTab.details.gdprErase.warningErased")}</div>
              <div>{t("customersTab.details.gdprErase.warningKept")}</div>
              <div>{t("customersTab.details.gdprErase.warningIrreversible")}</div>
            </>
          }
        />
        <Form form={gdprEraseForm} layout="vertical" onFinish={(values) => gdprEraseMutation.mutate(values)}>
          <Form.Item
            name="confirmEmail"
            label={t("customersTab.details.gdprErase.confirmLabel", {
              email: customer?.email ?? customer?.id ?? "",
            })}
            rules={[{ required: true, message: t("customersTab.details.gdprErase.confirmRequired") }]}
          >
            <Input placeholder={customer?.email ?? customer?.id ?? ""} autoComplete="off" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t("customersTab.details.gdprErase.resultTitle")}
        open={erasureResult !== null}
        onCancel={() => setErasureResult(null)}
        onOk={() => setErasureResult(null)}
        cancelButtonProps={{ style: { display: "none" } }}
        okText={t("common:ok", { defaultValue: "OK" })}
      >
        {erasureResult ? (
          <Space direction="vertical" size={12} style={{ width: "100%" }}>
            <Alert
              type={erasureResult.status === "COMPLETED" ? "success" : "warning"}
              showIcon
              message={
                erasureResult.status === "COMPLETED"
                  ? t("customersTab.details.gdprErase.resultCompleted")
                  : t("customersTab.details.gdprErase.resultCompletedWithErrors")
              }
            />
            <Table<ErasureReportEntry>
              rowKey="step"
              size="small"
              dataSource={erasureResult.report}
              pagination={false}
              showHeader={false}
            >
              <Table.Column<ErasureReportEntry> dataIndex="step" />
              <Table.Column<ErasureReportEntry>
                render={(_, row) => (
                  <Tag
                    color={
                      row.status === "failed"
                        ? "red"
                        : row.status === "kept"
                          ? "blue"
                          : row.status === "anonymized"
                            ? "orange"
                            : "green"
                    }
                  >
                    {t(`customersTab.details.gdprErase.status.${row.status}`, { defaultValue: row.status })}
                    {typeof row.count === "number" ? ` (${row.count})` : ""}
                  </Tag>
                )}
              />
            </Table>
          </Space>
        ) : null}
      </Modal>
    </Space>
  );
};

export default CustomerDetails;
